import { type ReactNode } from 'react';
import { Toast } from '../primitives';

/**
 * The four agent-write outcomes this toast can announce (Task 2.7, AC-8/9/11).
 *
 * - `update-document` / `update-work-item`: a plain confirmation, no link.
 * - `create-document` / `create-work-item`: the new item's title is
 *   interpolated into the copy and a click-through link routes to the new
 *   item's hash route.
 */
export type AgentWriteToastMode =
  | 'update-document'
  | 'update-work-item'
  | 'create-document'
  | 'create-work-item';

export interface AgentWriteToastProps {
  /** Which write outcome this toast announces. */
  mode: AgentWriteToastMode;
  /** New item title — required for the two `create-*` modes (interpolated). */
  title?: string;
  /**
   * New item id for the two `create-*` modes (click-through). May be absent on
   * the real path (the harness cannot know the id before the write commits) —
   * when absent the copy still renders but WITHOUT a click-through anchor.
   */
  id?: string;
  /** Auto-dismiss delay, delegated to the `<Toast>` primitive. Default 4000. */
  durationMs?: number;
  /** Fired by the `<Toast>` primitive on auto-dismiss or manual `×`. */
  onDismiss: () => void;
}

/**
 * Thin DOMAIN wrapper composing the global `<Toast>` primitive (Task 2.7).
 *
 * Surfaces the VERBATIM agent-write copy for four modes and, for the two
 * `create-*` modes, a click-through anchor to the new item's hash route
 * (`#/document-editor/<id>` / `#/work-item-detail/<id>`).
 *
 * Auto-dismiss (4000ms default) + the manual `×` button are DELEGATED to the
 * `<Toast>` primitive — `durationMs` + `onDismiss` are passed straight through;
 * neither is re-implemented here.
 *
 * Positioning (bottom-right of the editor shell) is the editor's
 * responsibility, NOT this wrapper — there is deliberately no positioning CSS
 * here. The `data-testid="agent-write-toast"` wrapper element carries no visual
 * styling; it is a transparent host for the composed primitive.
 */
export function AgentWriteToast({
  mode,
  title,
  id,
  durationMs = 4000,
  onDismiss
}: AgentWriteToastProps) {
  const body = renderBody(mode, title, id);

  return (
    <div data-testid="agent-write-toast">
      <Toast durationMs={durationMs} onDismiss={onDismiss}>
        {body}
      </Toast>
    </div>
  );
}

/** Build the toast body for a mode: plain copy, or copy + click-through link. */
function renderBody(
  mode: AgentWriteToastMode,
  title: string | undefined,
  id: string | undefined
): ReactNode {
  switch (mode) {
    case 'update-document':
      return 'Agent updated this document';
    case 'update-work-item':
      return 'Agent updated this work item';
    case 'create-document': {
      const copy = `Agent created a new document: '${title ?? ''}'`;
      // GRACEFUL createdId ABSENCE: on the real path the harness cannot know the
      // new id before the write commits, so `id` may be undefined. Show the copy
      // but render NO click-through anchor — never a broken `.../undefined` link.
      return id ? <a href={`#/document-editor/${id}`}>{copy}</a> : copy;
    }
    case 'create-work-item': {
      const copy = `Agent created a new work item: '${title ?? ''}'`;
      return id ? <a href={`#/work-item-detail/${id}`}>{copy}</a> : copy;
    }
    default:
      return null;
  }
}
